// src/components/admin/RecentAdminActivity.jsx
import React, { useState, useEffect } from 'react';
import {
  Database,
  RefreshCw,
  XCircle,
  Zap,
  Activity,
  Download,
  Loader2,
  Info,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { apiCall } from '../../utils/api';
import { isAdmin } from '../../utils/roles';
import AuditExportModal from '../modals/AuditExportModal.jsx';

const RECENT_LIMIT = 10;

// Read the current user's customer_id from their JWT in localStorage.
// Used to suppress redundant "organization #<own org>" target labels.
function getCurrentCustomerId() {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    return payload.customer_id || null;
  } catch {
    return null;
  }
}

export default function RecentAdminActivity() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    loadActivity();
  }, []);

  async function loadActivity() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(`/audit/admin-activity?limit=${RECENT_LIMIT}`);
      if (res.status === 403) {
        setError('Admin access required to view admin activity.');
        return;
      }
      if (!res.ok) {
        throw new Error('Failed to load admin activity');
      }
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (err) {
      console.error('Failed to load admin activity:', err);
      setError(err.message || 'Failed to load admin activity');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recent Admin Activity</CardTitle>
            {isAdmin() && (
              <button
                onClick={() => setExportOpen(true)}
                className="inline-flex items-center gap-2 text-sm text-blue-500 hover:text-blue-400 font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Activity className="w-10 h-10 text-[#6a6a6a] mb-3" />
              <p className="text-sm font-medium text-white mb-1">No admin activity yet</p>
              <p className="text-sm text-[#6a6a6a]">
                Administrative actions will appear here as they happen
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <ActivityRow
                  key={entry.id}
                  entry={entry}
                  ownCustomerId={getCurrentCustomerId()}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AuditExportModal isOpen={exportOpen} onClose={() => setExportOpen(false)} />
    </>
  );
}

// ==========================================
// ACTIVITY ROW
// ==========================================
function ActivityRow({ entry, ownCustomerId }) {
  const Icon = iconForAction(entry.action);
  const target = formatTarget(entry.target_type, entry.target_id, ownCustomerId);

  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#2a2a2a] last:border-0">
      <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-white">
            {humanizeAction(entry.action)}
          </p>
          {target && (
            <>
              <span className="text-xs text-[#6a6a6a]">·</span>
              <p className="text-sm text-blue-400">{target}</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-xs text-[#6a6a6a]">by {entry.actor_email || 'unknown'}</p>
          <span className="text-xs text-[#6a6a6a]">·</span>
          <p className="text-xs text-[#6a6a6a]">{formatTimestamp(entry.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// HELPERS
// ==========================================

// Turn a snake_case action code into a readable label.
// e.g. "connector_created" -> "Connector created"
function humanizeAction(action) {
  if (!action) return 'Action';
  const spaced = action.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Build a target label from type + id, e.g. "connector #1".
// The log stores IDs, not display names, so we show the ID honestly.
function formatTarget(targetType, targetId, ownCustomerId) {
  if (!targetType && !targetId) return '';
  // Suppress the target when it's the user's own organization. The user
  // is looking at their own org's admin page; saying "organization #<own
  // org uuid>" on every row is noise. Targets that genuinely add
  // information (specific connectors, users, etc.) still render.
  if (
    targetType === 'organization' &&
    ownCustomerId &&
    targetId &&
    String(targetId) === String(ownCustomerId)
  ) {
    return '';
  }
  // Truncate long IDs (UUIDs especially) for display readability when
  // the target IS shown. The full ID is still in the audit log and the
  // CSV export.
  const shortId = targetId && targetId.length > 12
    ? targetId.slice(0, 8) + '…'
    : targetId;
  if (targetType && shortId) return `${targetType} #${shortId}`;
  return targetType || `#${shortId}`;
}

function iconForAction(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('creat') || a.includes('add') || a.includes('grant')) return Database;
  if (a.includes('updat') || a.includes('refresh') || a.includes('rotat')) return RefreshCw;
  if (a.includes('delet') || a.includes('deactivat') || a.includes('revok') || a.includes('disabl')) return XCircle;
  if (a.includes('auth') || a.includes('login') || a.includes('token')) return Zap;
  return Activity;
}

function formatTimestamp(isoString) {
  if (!isoString) return 'Unknown';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}